import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Loader from "../../Components/Loader/Loader";
import { serviceConsumer } from "../../network/ServiceConsumer";
import Help from "../../Components/Help/Help";
import EventSite from "../EventSite/EventSite";
import { oireachtasState } from "../EventSite/eventSiteModel";
import { isEventToday } from "../../helper/eventFeed";

// T0 — the Oireachtas event info page (about, collaborators, map), on the
// shared parent event page (MC2 ecbc86f7) like FeiseannazViewEvent, so all
// three event types share the same dashboard → about page → register flow. Differences from the feis page:
// registration is teacher-only (parents see no Register button; the button
// routes to the Oireachtas event hub /oireachtas/:id rather than straight
// into a registration form). After the close, Competitors opens the running
// order (the Competitors page, card beb095f8).

const OireachtasViewEvent = () => {
  const [loading, setLoading] = useState(true);
  const [eventProp, setEventProp] = useState();
  const [collaborators, setCollaborators] = useState();

  const navigate = useNavigate();
  const { state, search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const eventId = searchParams.get("id");
  const wrapperRef = useRef(null);

  //getting event details from passed values
  // a shared or typed link has no router state: use the event's own dates
  const routerFrom = state?.from;
  const isTeacher = localStorage.getItem("isTeacher") === "true"; // set at SignIn

  useEffect(() => {
    wrapperRef.current?.scrollTo({ top: -50, behavior: "smooth" });
    getInitialDetails();
  }, []);

  //button handlers
  const backToViewEvent = () => {
    localStorage.getItem("accessToken") ? navigate("/parent") : navigate("/");
  };

  const goToRegisgternow = () => {
    if (localStorage.getItem("accessToken")) {
      navigate(`/oireachtas/${eventId}`, { state: { event: eventProp } });
    } else {
      storeOnRegisterClick(); //in local for redirection
      navigate("/login", { replace: true });
    }
  };

  // After the close, a teacher still needs their school's entries.
  const goToMyEntries = () => navigate(`/teacher/oireachtas/${eventId}/entries`);

  // The running order (counts until the draw), signed in like a feis's list.
  const goToCompetitors = () => {
    const path = `/competitors/${(eventProp?.name || "event").split(" ").join("+")}?id=${eventId}&kind=oireachtas`;
    if (localStorage.getItem("accessToken")) {
      navigate(path, { state: { event: eventProp } });
      return;
    }
    localStorage.setItem("path", path);
    localStorage.setItem("eventId", eventProp?._id);
    localStorage.setItem("eventType", "Oireachtas");
    localStorage.setItem("from", event);
    localStorage.setItem("isCompetitor", true);
    navigate("/login");
  };

  //to get event and collaborator data (the event read is public)
  const getInitialDetails = async () => {
    try {
      setLoading(true);
      const eventUrl = `${process.env.REACT_APP_BASE_URL}/oireachtas/${eventId}`;
      const eventResponse = await serviceConsumer("GET", eventUrl);
      setEventProp(eventResponse.event);
      document.title = `Feis.link - ${eventResponse?.event?.name}`;
      const collaboratorsUrl = `${process.env.REACT_APP_BASE_URL}/feis/collaborators/${eventId}`;
      const collaboratorsResponse = await serviceConsumer("GET", collaboratorsUrl);
      setCollaborators(collaboratorsResponse.result);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      navigate("pageNotFound", { replace: true });
    }
  };

  const storeOnRegisterClick = () => {
    const urlEventName = eventProp?.name?.split(" ").join("+");
    localStorage.setItem("path", `/oireachtas/event/${urlEventName}?id=${eventProp?._id}`);
    localStorage.setItem("eventId", eventProp?._id);
    localStorage.setItem("eventType", eventProp?.eventType);
    localStorage.setItem("from", "open");
  };

  const event = routerFrom || oireachtasState(eventProp);
  const signedIn = !!localStorage.getItem("accessToken");

  // registration is teacher-only: parents see the event, not a Register button.
  // The syllabus and schedule buttons come from EventSite itself.
  const urlEventName = (eventProp?.name || "event").split(" ").join("+");
  const today = event === "today" || (!!eventProp && isEventToday({ ...eventProp, eventType: "Oireachtas" }));
  const afterClose = event === "closed" || today || event === "result";
  const goToResults = () => navigate(`/oireachtas/results/${urlEventName}?id=${eventId}`, { state: { event: eventProp } });
  const actions = eventProp ? [
    event === "upcoming" && isTeacher && { key: "register", icon: "how_to_reg", primary: true, disabled: true, label: "Registration not open yet" },
    event === "open" && isTeacher && { key: "register", icon: "how_to_reg", primary: true, label: "Register", onClick: goToRegisgternow },
    // closed: the card's own line already says so, so no disabled "Entries
    // closed" button (Devin 2026-10-30)
    // after the close a teacher still needs their school's entries, on the days and after
    afterClose && isTeacher && signedIn && { key: "entries", icon: "list_alt", primary: event === "closed", label: "View my entries", onClick: goToMyEntries },
    // on the days (Devin 2026-10-30): three main buttons, Competitions,
    // Schedule (once there is one to open) and Recalls & results
    today && { key: "counts", icon: "bar_chart", primary: true, label: "Competitions", onClick: goToCompetitors },
    today && eventProp.schedulePdfUrl && { key: "schedule", icon: "calendar_month", primary: true, label: "Schedule", onClick: () => window.open(eventProp.schedulePdfUrl, "_blank", "noopener,noreferrer") },
    today && { key: "results", icon: "emoji_events", primary: true, label: "Recalls & results", onClick: goToResults },
    !today && afterClose && { key: "counts", icon: "bar_chart", label: "Competitors", onClick: goToCompetitors },
    // never alongside the days' Recalls & results (same key; MC review a6f8407d R1-F2)
    !today && event === "result" && { key: "results", icon: "emoji_events", primary: true, label: "View results", onClick: goToResults },
  ].filter(Boolean) : [];

  return (
    <div ref={wrapperRef}>
      {loading && <Loader />}
      {!loading && eventProp && (
        <EventSite
          event={eventProp}
          kind="oireachtas"
          state={today ? "today" : event}
          actions={actions}
          collaborators={collaborators}
          onBack={backToViewEvent}
        />
      )}
      <Help />
    </div>
  );
};

export default OireachtasViewEvent;
